/* eslint-disable jsx-a11y/iframe-has-title */
import { memo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const isImage = (f) => /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(f || '');
const isPdf = (f) => /\.pdf$/i.test(f || '');
const fileName = (f) => (f || '').split('/').pop();

/**
 * Shartnomaga to'g'ridan-to'g'ri yuklangan fayllar — "Topshiriqlar" ostida.
 * Rasm → galereya + preview, PDF → preview (iframe), boshqa → yuklab olish.
 */
const ContractFilesSection = ({ files = [] }) => {
  const { t } = useTranslation();
  const [previewImg, setPreviewImg] = useState(null);
  const [previewPdf, setPreviewPdf] = useState(null);

  if (!files?.length) return null;

  const url = (f) => `${process.env.REACT_APP_BACKEND_URL}/${f}`;
  const images = files.filter((f) => isImage(f.url));
  const pdfs = files.filter((f) => isPdf(f.url));
  const others = files.filter((f) => !isImage(f.url) && !isPdf(f.url));

  const metaLine = (file) => (
    <span className='flex items-center gap-x-3 text-[11px] text-gray-500 flex-wrap'>
      {file.user?.name && (
        <span>
          <i className='bi bi-person mr-1' />
          {file.user.name}
        </span>
      )}
      {file.date && (
        <span>
          <i className='bi bi-calendar mr-1' />
          {moment(file.date).format('DD.MM.YYYY')}
        </span>
      )}
    </span>
  );

  return (
    <div className='mt-4'>
      <Accordion
        defaultExpanded
        disableGutters
        className='!rounded-xl !border !border-gray-200 !shadow-none before:!hidden'
      >
        <AccordionSummary
          expandIcon={<i className='bi bi-chevron-down' />}
          className='!bg-gray-100 !rounded-t-xl'
        >
          <span className='font-medium'>
            <i className='bi bi-paperclip mr-2' />
            {t('fields.uploadedFiles')} ({files.length})
          </span>
        </AccordionSummary>
        <AccordionDetails className='!p-4'>
          <div className='flex flex-col gap-4'>
            {/* Rasmlar — galereya */}
            {images.length > 0 && (
              <div className='flex flex-row gap-3 flex-wrap'>
                {images.map((file) => (
                  <button
                    key={file.id}
                    type='button'
                    onClick={() => setPreviewImg(url(file.url))}
                    className='group flex flex-col gap-1 items-center'
                  >
                    <span className='relative'>
                      <img
                        src={url(file.url)}
                        alt={file.name || ''}
                        className='h-24 w-24 rounded-lg object-cover border border-gray-200 group-hover:opacity-80 transition'
                      />
                      <span className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition'>
                        <i className='bi bi-zoom-in text-white text-lg drop-shadow' />
                      </span>
                    </span>
                    <span className='text-[10px] text-gray-500 truncate max-w-[96px]'>
                      {file.name || fileName(file.url)}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* PDF — kartalar */}
            {pdfs.length > 0 && (
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                {pdfs.map((file) => (
                  <button
                    key={file.id}
                    type='button'
                    onClick={() => setPreviewPdf(url(file.url))}
                    className='flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left hover:border-red-300 hover:bg-red-50/40 transition'
                  >
                    <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500'>
                      <i className='bi bi-file-earmark-pdf text-xl' />
                    </span>
                    <span className='flex flex-col min-w-0 flex-1'>
                      <span className='text-sm font-medium text-gray-800 truncate'>
                        {file.name || fileName(file.url)}
                      </span>
                      {metaLine(file)}
                    </span>
                    <i className='bi bi-eye text-gray-400' />
                  </button>
                ))}
              </div>
            )}

            {/* Boshqa fayllar — yuklab olish */}
            {others.map((file) => (
              <div
                key={file.id}
                className='flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2'
              >
                <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-gray-500'>
                  <i className='bi bi-file-earmark text-xl' />
                </span>
                <span className='flex flex-col min-w-0 flex-1'>
                  <span className='text-sm font-medium text-gray-800 truncate'>
                    {file.name || fileName(file.url)}
                  </span>
                  {metaLine(file)}
                </span>
                <a
                  href={url(file.url)}
                  target='_blank'
                  rel='noreferrer'
                  className='text-blue-500 hover:text-blue-700 shrink-0'
                >
                  <i className='bi bi-download text-lg' />
                </a>
              </div>
            ))}
          </div>
        </AccordionDetails>
      </Accordion>

      {/* Rasm preview */}
      <Dialog
        open={!!previewImg}
        onClose={() => setPreviewImg(null)}
        maxWidth='lg'
        PaperProps={{
          className: '!bg-transparent !shadow-none',
          style: { margin: 8 },
        }}
      >
        <div className='relative'>
          <IconButton
            size='small'
            onClick={() => setPreviewImg(null)}
            className='!absolute !top-2 !right-2 !bg-black/50 !text-white hover:!bg-black/70'
          >
            <i className='bi bi-x text-xl' />
          </IconButton>
          <img
            src={previewImg}
            alt=''
            className='max-w-[90vw] max-h-[90vh] rounded-xl object-contain'
          />
        </div>
      </Dialog>

      {/* PDF preview */}
      <Dialog
        open={!!previewPdf}
        onClose={() => setPreviewPdf(null)}
        maxWidth='lg'
        fullWidth
        PaperProps={{ className: '!rounded-xl !overflow-hidden' }}
      >
        <div className='flex items-center justify-between bg-gray-100 px-3 py-2'>
          <span className='text-sm font-medium text-gray-700 truncate'>
            <i className='bi bi-file-earmark-pdf text-red-500 mr-2' />
            {fileName(previewPdf)}
          </span>
          <span className='flex items-center gap-1'>
            <a
              href={previewPdf || '#'}
              target='_blank'
              rel='noreferrer'
              className='text-gray-500 hover:text-gray-800 px-2'
              title='Yangi oynada ochish'
            >
              <i className='bi bi-box-arrow-up-right' />
            </a>
            <IconButton size='small' onClick={() => setPreviewPdf(null)}>
              <i className='bi bi-x text-xl' />
            </IconButton>
          </span>
        </div>
        {previewPdf && (
          <iframe
            src={previewPdf}
            className='w-full'
            style={{ height: '80vh', border: 'none' }}
          />
        )}
      </Dialog>
    </div>
  );
};

export default memo(ContractFilesSection);
